import React from 'react';
import { Box, CircularProgress, Paper, Typography } from '@/ui';
import SpecialScreen from '@/engine/renderer/SpecialScreen';
import type { OpenActionSpec } from '@/shared/warnings/types';

interface BudgetFormRendererProps {
  isBudgetDataLoading: boolean;
  currentSchema: any;
  currentFormId: string;
  initialFormData: Record<string, unknown>;
  /**
   * When incremented, forces SpecialScreen to remount so react-hook-form
   * rehydrates from `initialFormData` (used after confirm-before-clear).
   */
  renderNonce?: number;
  selectedInner: string;
  handleFormSubmit: (data: unknown) => Promise<void>;
  handleBudgetFormValuesChange: (vals: unknown) => void;
  handleWarningDialog: (action?: OpenActionSpec, metadata?: { varId?: string }) => boolean;
  /** When true, transforms "council tax" to "council rates" for NI users */
  isNorthernIreland?: boolean;
}

const BudgetFormRenderer: React.FC<BudgetFormRendererProps> = ({
  isBudgetDataLoading,
  currentSchema,
  currentFormId,
  initialFormData,
  renderNonce = 0,
  selectedInner,
  handleFormSubmit,
  handleBudgetFormValuesChange,
  handleWarningDialog,
  isNorthernIreland = false,
}) => {
  if (isBudgetDataLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          gap: 2
        }}
      >
        <CircularProgress size={28} sx={{ color: '#155F54' }} />
        <Typography variant="body2" color="text.secondary">
          Loading budget data…
        </Typography>
      </Paper>
    );
  }

  if (!currentSchema) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Schema not available for {selectedInner}
        </Typography>
      </Paper>
    );
  }


  return (
    <SpecialScreen
      key={`${currentFormId}::${renderNonce}`}
      schema={currentSchema as unknown as Parameters<typeof SpecialScreen>[0]['schema']}
      hideHeader={true}
      hideActions={true}
      initialData={initialFormData}
      onSubmit={handleFormSubmit}
      onValuesChange={handleBudgetFormValuesChange}
      formId={currentFormId}
      onWarningAction={handleWarningDialog}
      isNorthernIreland={isNorthernIreland}
    />
  );
};

export default BudgetFormRenderer;

